import {
  Badge,
  Box,
  Button,
  HStack,
  Heading,
  Input,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClientPrincipal, authConfig, getUserInfo, AUTHORIZED_EMAIL } from "../authConfig";
import { bookingService } from "../services/bookingService";
import { eventService } from "../services/eventService";
import PassportModal from "../components/PassportModal";
import { ListaAttesa, Prenotazione } from "../types/Booking";
import { Evento } from "../types/Event";

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);

const MyBookings: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<ClientPrincipal | null>(null);
  const [loading, setLoading] = useState(true);
  const [prenotazioni, setPrenotazioni] = useState<Prenotazione[]>([]);
  const [listaAttesa, setListaAttesa] = useState<ListaAttesa[]>([]);
  const [passportOpen, setPassportOpen] = useState(false);
  const [visitedEvents, setVisitedEvents] = useState<Evento[]>([]);
  const [allEvents, setAllEvents] = useState<Evento[]>([]);
  const [impersonateEmail, setImpersonateEmail] = useState("");
  const [viewAsEmail, setViewAsEmail] = useState("");

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUser = async () => {
    const userInfo = await getUserInfo();
    setUser(userInfo);
    
    if (userInfo?.userDetails) {
      await loadBookings(userInfo.userDetails);
    }
    setLoading(false);
  };

  const handleViewAsUser = () => {
    if (impersonateEmail.trim()) {
      const email = impersonateEmail.trim().toUpperCase();
      setViewAsEmail(email);
      loadBookings(email);
    } else {
      setViewAsEmail("");
      if (user?.userDetails) {
        loadBookings(user.userDetails);
      }
    }
  };

  const loadBookings = async (email: string) => {
    try {
      const data = await bookingService.getMyBookings(email);
      setPrenotazioni(data.prenotazioni);
      setListaAttesa(data.listaAttesa);
      
      // Load all events to get full event details for passport
      const eventsData = await eventService.getEventi();
      setAllEvents(eventsData);
      const now = new Date();
      
      // Get confirmed past bookings event IDs
      const pastEventIds = data.prenotazioni
        .filter(p => p.Stato === 'Confermata' && p.EventoId && new Date(p.DataEvento || '') < now)
        .map(p => p.EventoId as number);
      
      // Filter events that user attended
      const visitedEventsData = eventsData.filter(e => 
        e.EventoId && pastEventIds.includes(e.EventoId)
      );
      setVisitedEvents(visitedEventsData);
    } catch (err) {
      console.error("Errore nel caricamento delle prenotazioni:", err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (stato?: string) => {
    switch (stato) {
      case "Confermata":
        return <Badge colorScheme="green" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">Confermata</Badge>;
      case "Annullata":
        return <Badge colorScheme="red" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">Annullata</Badge>;
      case "InAttesa":
        return <Badge colorScheme="yellow" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">In Attesa</Badge>;
      case "Invitato":
        return <Badge colorScheme="blue" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">Invitato</Badge>;
      default:
        return <Badge fontSize="2xs" textTransform="uppercase" letterSpacing="wide">{stato}</Badge>;
    }
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.900" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="lg" color="white" thickness="1px" />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box minH="100vh" bg="gray.900" py={16} px={6}>
        <VStack spacing={6} maxW="700px" mx="auto">
          <Heading color="white" textTransform="uppercase" letterSpacing="widest" fontSize="2xl" fontWeight="400">
            Accesso Richiesto
          </Heading>
          <Text color="gray.400" textAlign="center" textTransform="uppercase" letterSpacing="wide" fontSize="sm">
            Effettua l'accesso per visualizzare le tue prenotazioni
          </Text>
          <Button
            bg="white"
            color="gray.900"
            borderRadius="none"
            fontSize="xs"
            letterSpacing="wide"
            textTransform="uppercase"
            _hover={{ bg: "gray.200" }}
            onClick={() => window.location.href = `${authConfig.loginUrl}?post_login_redirect_uri=${encodeURIComponent(window.location.pathname)}`}
          >
            ACCEDI
          </Button>
        </VStack>
      </Box>
    );
  }

  const activePrenotazioni = prenotazioni.filter(p => p.Stato !== "Annullata");
  const activeListaAttesa = listaAttesa.filter(l => l.Stato !== "Annullata");

  // Separate future and past bookings
  const now = new Date();
  const futurePrenotazioni = activePrenotazioni.filter(p => new Date(p.DataEvento || '') >= now);
  const pastPrenotazioni = activePrenotazioni.filter(p => new Date(p.DataEvento || '') < now);

  return (
    <>
      {/* Fixed background image */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundImage="url('/tavolata.jpg')"
        backgroundSize="cover"
        backgroundPosition="center"
        zIndex={-2}
      />
      {/* Fixed dark overlay */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.800"
        zIndex={-1}
      />
      
    <Box minH="100vh" position="relative" zIndex={1}>
      {/* Header */}
      <Box px={6} py={4}>
        <HStack justify="space-between" align="flex-start">
          <Heading
            as="a"
            href="/"
            fontSize={{ base: "md", md: "lg" }}
            fontWeight="500"
            color="white"
            textTransform="uppercase"
            letterSpacing="wider"
            _hover={{ textDecoration: "none" }}
          >
            Scarpetta Home Restaurant
          </Heading>
          {/* Admin Impersonation */}
          {user?.userDetails === AUTHORIZED_EMAIL && (
            <HStack spacing={2} maxW="400px">
              <Input
                value={impersonateEmail}
                onChange={(e) => setImpersonateEmail(e.target.value)}
                placeholder="Email utente"
                bg="rgba(0, 0, 0, 0.3)"
                border="1px solid"
                borderColor="whiteAlpha.300"
                borderRadius="none"
                color="white"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "white", boxShadow: "none" }}
                size="xs"
                fontSize="xs"
              />
              <Button
                onClick={handleViewAsUser}
                size="xs"
                bg="transparent"
                color="gray.400"
                border="1px solid"
                borderColor="whiteAlpha.300"
                borderRadius="none"
                fontSize="xs"
                letterSpacing="wide"
                textTransform="uppercase"
                _hover={{ bg: "whiteAlpha.100", color: "white" }}
                minW="60px"
              >
                {viewAsEmail ? "RESET" : "SIMULA"}
              </Button>
              {viewAsEmail && (
                <Text color="cyan.400" fontSize="2xs" whiteSpace="nowrap">
                  👀 {viewAsEmail}
                </Text>
              )}
            </HStack>
          )}
        </HStack>
      </Box>

      <Box py={{ base: 6, md: 16 }} px={6}>
        <VStack spacing={{ base: 2, md: 4 }} mb={12} maxW="700px" mx="auto" align="flex-start">
          {/* Back button */}
          <Button
            bg="transparent"
            color="gray.400"
            borderRadius="none"
            fontSize="xs"
            letterSpacing="wide"
            textTransform="uppercase"
            _hover={{ color: "white" }}
            onClick={() => navigate("/")}
            pl={0}
          >
            ← INDIETRO
          </Button>

          {/* Title */}
          <HStack w="full" justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <Box>
              <MotionHeading
                as="h1"
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="500"
                letterSpacing="wider"
                color="white"
                textTransform="uppercase"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                Le Mie Prenotazioni
              </MotionHeading>
            </Box>
            
            {/* Passport Button */}
            <Button
              onClick={() => setPassportOpen(true)}
              px={{ base: 3, md: 6 }}
              py={{ base: 2, md: 3 }}
              bg="transparent"
              border="1px solid"
              borderColor="whiteAlpha.300"
              borderRadius="none"
              w={{ base: "160px", md: "180px" }}
              transition="all 0.3s ease"
              color="white"
              fontSize={{ base: "2xs", md: "xs" }}
              fontWeight="400"
              letterSpacing="widest"
              _hover={{
                bg: "white",
                borderColor: "white",
                color: "gray.900",
              }}
              leftIcon={
                <Box as="svg" width={{ base: "14px", md: "16px" }} height={{ base: "14px", md: "16px" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                </Box>
              }
            >
              PASSAPORTO
            </Button>
          </HStack>
          <MotionBox
            w={{ base: "40px", md: "60px" }}
            h="1px"
            bg="white"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
      </VStack>

      <VStack spacing={12} maxW="700px" mx="auto">
        {/* Prenotazioni Future */}
        <Box w="full">
          <Text
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="xs"
            mb={6}
          >
            PRENOTAZIONI FUTURE ({futurePrenotazioni.length})
          </Text>

          {futurePrenotazioni.length === 0 ? (
            <Text color="gray.600" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
              Nessuna prenotazione futura
            </Text>
          ) : (
            <VStack spacing={4} align="stretch">
              {futurePrenotazioni.map((prenotazione, index) => (
                <MotionBox
                  key={prenotazione.PrenotazioneId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  p={6}
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                >
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between" align="flex-start">
                      <Text color="white" fontWeight="500" textTransform="uppercase" letterSpacing="wide" fontSize="sm">
                        {prenotazione.EventoTitolo}
                      </Text>
                      {getStatusBadge(prenotazione.Stato)}
                    </HStack>
                    
                    <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                      {formatDate(prenotazione.DataEvento || "")}
                    </Text>

                    <HStack spacing={6} pt={2}>
                      <VStack align="flex-start" spacing={0}>
                        <Text color="gray.600" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                          Persone
                        </Text>
                        <Text color="white" fontSize="sm">
                          {prenotazione.NumeroPersone}
                        </Text>
                      </VStack>
                      <VStack align="flex-start" spacing={0}>
                        <Text color="gray.600" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                          Analcolico
                        </Text>
                        <Text color="white" fontSize="sm">
                          {prenotazione.NumeroAnalcolico}
                        </Text>
                      </VStack>
                      <VStack align="flex-start" spacing={0}>
                        <Text color="gray.600" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                          Alcolico
                        </Text>
                        <Text color="white" fontSize="sm">
                          {prenotazione.NumeroAlcolico}
                        </Text>
                      </VStack>
                      {prenotazione.GiftCardCodice && (
                        <VStack align="flex-start" spacing={0}>
                          <Text color="gray.600" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                            Gift Card
                          </Text>
                          <Badge colorScheme="purple" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                            {prenotazione.GiftCardCodice}
                          </Badge>
                        </VStack>
                      )}
                    </HStack>

                    <HStack pt={2} spacing={4}>
                      <Button
                          size="xs"
                          bg="transparent"
                          color="gray.400"
                          border="1px solid"
                          borderColor="whiteAlpha.300"
                          borderRadius="none"
                          fontSize="2xs"
                          letterSpacing="wide"
                          textTransform="uppercase"
                          _hover={{ bg: "whiteAlpha.100", color: "white" }}
                          onClick={() => navigate(`/modifica/${prenotazione.PrenotazioneId}`)}
                        >
                          MODIFICA
                        </Button>
                        <Button
                          size="xs"
                          bg="transparent"
                          color="red.400"
                          border="1px solid"
                          borderColor="red.400"
                          borderRadius="none"
                          fontSize="2xs"
                          letterSpacing="wide"
                          textTransform="uppercase"
                          _hover={{ bg: "red.400", color: "white" }}
                          onClick={() => navigate(`/annulla/${prenotazione.PrenotazioneId}`)}
                        >
                          ANNULLA
                        </Button>
                      </HStack>
                  </VStack>
                </MotionBox>
              ))}
            </VStack>
          )}
        </Box>

        {/* Prenotazioni Passate */}
        {pastPrenotazioni.length > 0 && (
          <Box w="full">
            <Text
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="widest"
              fontSize="xs"
              mb={6}
            >
              PRENOTAZIONI PASSATE ({pastPrenotazioni.length})
            </Text>

            <VStack spacing={4} align="stretch">
              {pastPrenotazioni.map((prenotazione, index) => (
                <MotionBox
                  key={prenotazione.PrenotazioneId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  p={6}
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  opacity={0.6}
                  bg="whiteAlpha.50"
                >
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between" align="flex-start">
                      <Text color="white" fontWeight="500" textTransform="uppercase" letterSpacing="wide" fontSize="sm">
                        {prenotazione.EventoTitolo}
                      </Text>
                      <Badge colorScheme="gray" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                        Completata
                      </Badge>
                    </HStack>
                    
                    <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                      {formatDate(prenotazione.DataEvento || "")}
                    </Text>

                    <HStack spacing={6} pt={2}>
                      <VStack align="flex-start" spacing={0}>
                        <Text color="gray.600" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                          Persone
                        </Text>
                        <Text color="white" fontSize="sm">
                          {prenotazione.NumeroPersone}
                        </Text>
                      </VStack>
                      <VStack align="flex-start" spacing={0}>
                        <Text color="gray.600" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                          Analcolico
                        </Text>
                        <Text color="white" fontSize="sm">
                          {prenotazione.NumeroAnalcolico}
                        </Text>
                      </VStack>
                      <VStack align="flex-start" spacing={0}>
                        <Text color="gray.600" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                          Alcolico
                        </Text>
                        <Text color="white" fontSize="sm">
                          {prenotazione.NumeroAlcolico}
                        </Text>
                      </VStack>
                      {prenotazione.GiftCardCodice && (
                        <VStack align="flex-start" spacing={0}>
                          <Text color="gray.600" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                            Gift Card
                          </Text>
                          <Badge colorScheme="purple" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                            {prenotazione.GiftCardCodice}
                          </Badge>
                        </VStack>
                      )}
                    </HStack>
                  </VStack>
                </MotionBox>
              ))}
            </VStack>
          </Box>
        )}

        {/* Lista d'attesa */}
        <Box w="full">
          <Text
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="xs"
            mb={6}
          >
            LISTA D'ATTESA ({activeListaAttesa.length})
          </Text>

          {activeListaAttesa.length === 0 ? (
            <Text color="gray.600" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
              Non sei in nessuna lista d'attesa
            </Text>
          ) : (
            <VStack spacing={4} align="stretch">
              {activeListaAttesa.map((attesa, index) => (
                <MotionBox
                  key={attesa.ListaAttesaId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  p={6}
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                >
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between" align="flex-start">
                      <Text color="white" fontWeight="500" textTransform="uppercase" letterSpacing="wide" fontSize="sm">
                        {attesa.EventoTitolo}
                      </Text>
                      {getStatusBadge(attesa.Stato)}
                    </HStack>
                    
                    <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                      {formatDate(attesa.DataEvento || "")}
                    </Text>

                    <HStack spacing={6} pt={2}>
                      <VStack align="flex-start" spacing={0}>
                        <Text color="gray.600" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                          Persone
                        </Text>
                        <Text color="white" fontSize="sm">
                          {attesa.NumeroPersone}
                        </Text>
                      </VStack>
                      <VStack align="flex-start" spacing={0}>
                        <Text color="gray.600" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                          Analcolico
                        </Text>
                        <Text color="white" fontSize="sm">
                          {attesa.NumeroAnalcolico}
                        </Text>
                      </VStack>
                      <VStack align="flex-start" spacing={0}>
                        <Text color="gray.600" fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
                          Alcolico
                        </Text>
                        <Text color="white" fontSize="sm">
                          {attesa.NumeroAlcolico}
                        </Text>
                      </VStack>
                    </HStack>

                    <HStack pt={2} spacing={4}>
                      <Button
                        size="xs"
                        bg="transparent"
                        color="gray.400"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        borderRadius="none"
                        fontSize="2xs"
                        letterSpacing="wide"
                        textTransform="uppercase"
                        _hover={{ bg: "whiteAlpha.100", color: "white" }}
                        onClick={() => navigate(`/modifica-attesa/${attesa.ListaAttesaId}`)}
                      >
                        MODIFICA
                      </Button>
                      <Button
                          size="xs"
                          bg="transparent"
                          color="red.400"
                          border="1px solid"
                          borderColor="red.400"
                          borderRadius="none"
                          fontSize="2xs"
                          letterSpacing="wide"
                          textTransform="uppercase"
                          _hover={{ bg: "red.400", color: "white" }}
                          onClick={() => navigate(`/annulla-attesa/${attesa.ListaAttesaId}`)}
                        >
                          ANNULLA
                        </Button>
                      </HStack>
                  </VStack>
                </MotionBox>
              ))}
            </VStack>
          )}
        </Box>
      </VStack>
      </Box>
      
      {/* Passport Modal */}
      <PassportModal
        isOpen={passportOpen}
        onClose={() => setPassportOpen(false)}
        visitedEvents={visitedEvents}
        allEvents={allEvents}
      />
    </Box>
    </>
  );
};

export default MyBookings;
